import type { CSSProperties } from 'react';

import type { MenuData, Template, TemplateThemeMode } from '@/app/dashboard/templates/components/types/template';
import { fonts } from '@/lib/types';
import { resolveTemplateVariables } from './templateVariables';
import { cornersOfRectangle } from '@dnd-kit/core/dist/utilities/algorithms/helpers';
import { toHslTriplet } from '@/lib/menu-themes/hsl';
import { colorReadableWithForeground, contrastRatio as colordContrastRatio, readableForeground } from '@/lib/menu-themes/contrast';

export type TemplateSurfacePalette = {
  background: string;
  foreground: string;
  muted: string;
  mutedForeground: string;
  card: string;
  cardForeground: string;
  border: string;
  accent: string;
  accentForeground: string;
  primary: string;
  primaryForeground: string;
  promoBg: string;
  promoText: string;
  promoBorder: string; 
  panelGlass: string;
  panelShadow: string;
  overlay: string;
};

/** Keep this pure palette module server-safe; `util` is a client barrel. */
function normalizeFontFamily(fontFamily: string): string {
  if (!fontFamily || typeof fontFamily !== 'string') return fontFamily;
  const match = fontFamily.match(/^['"]?([^'",]+)['"]?/);
  const fontName = match?.[1]?.trim();
  if (!fontName) return fontFamily;
  const font = (fonts as readonly { name: string; css: string }[]).find(
    (candidate) => candidate.name.toLowerCase() === fontName.toLowerCase(),
  );
  return font ? `var(--${font.css}, ${fontFamily}), ${fontFamily}` : fontFamily;
}

function pickString(candidate: unknown, fallback: string) {
  return typeof candidate === 'string' && candidate.trim() ? candidate.trim() : fallback;
}

function parseHexColor(value: string): { r: number; g: number; b: number } | null {
  const normalized = value.trim().replace(/^#/, '');
  if (!/^[\da-f]{3}([\da-f]{3})?$/i.test(normalized)) return null;

  const expanded = normalized.length === 3
    ? normalized.split('').map((part) => part + part).join('')
    : normalized;

  return {
    r: parseInt(expanded.slice(0, 2), 16),
    g: parseInt(expanded.slice(2, 4), 16),
    b: parseInt(expanded.slice(4, 6), 16),
  };
}

function parseNamedColor(value: string): { r: number; g: number; b: number } | null {
  const normalized = value.trim().toLowerCase();
  if (normalized === 'black') return { r: 0, g: 0, b: 0 };
  if (normalized === 'white') return { r: 255, g: 255, b: 255 };
  return null;
}

function parseRgbColor(value: string): { r: number; g: number; b: number } | null {
  const match = value.trim().match(/^rgba?\(([^)]+)\)$/i);
  if (!match) return null;

  const channels = match[1]
    .split(',')
    .slice(0, 3)
    .map((part) => Number(part.trim().replace('%', '')));

  if (channels.length < 3 || channels.some((channel) => !Number.isFinite(channel))) {
    return null;
  }

  const usesPercent = match[1].includes('%');
  const normalize = (channel: number) =>
    Math.max(0, Math.min(255, usesPercent ? Math.round((channel / 100) * 255) : Math.round(channel)));

  return { r: normalize(channels[0]), g: normalize(channels[1]), b: normalize(channels[2]) };
}

function parseCssColor(value: string): { r: number; g: number; b: number } | null {
  return parseNamedColor(value) || parseHexColor(value) || parseRgbColor(value);
}

function toHexColor({ r, g, b }: { r: number; g: number; b: number }) {
  const toHex = (channel: number) =>
    Math.max(0, Math.min(255, Math.round(channel))).toString(16).padStart(2, '0');
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

function mixCssColors(base: string, basePercent: number, blend: string, blendPercent: number, fallback: string) {
  const parsedBase = parseCssColor(base);
  const parsedBlend = parseCssColor(blend);
  const total = basePercent + blendPercent;
  if (!parsedBase || !parsedBlend || total <= 0) {
    return fallback;
  }

  const baseWeight = basePercent / total;
  const blendWeight = blendPercent / total;
  return toHexColor({
    r: parsedBase.r * baseWeight + parsedBlend.r * blendWeight,
    g: parsedBase.g * baseWeight + parsedBlend.g * blendWeight,
    b: parsedBase.b * baseWeight + parsedBlend.b * blendWeight,
  });
}

function contrastRatio(colorA: string, colorB: string) {
  const parsedA = parseCssColor(colorA);
  const parsedB = parseCssColor(colorB);
  if (!parsedA || !parsedB) return null;

  return colordContrastRatio(colorA, colorB);
}

export function resolveReadableTextColor(background: string, preferred?: string | null) {
  return readableForeground(background, preferred);
}

export function resolveTemplateSurfacePalette(
  template: Template | undefined | null,
  mode: TemplateThemeMode = 'light',
): TemplateSurfacePalette {
  const resolvedVariables = template ? resolveTemplateVariables(template, mode) : undefined;
  
  const colors = (resolvedVariables as Record<string, any> | undefined)?.colors || {};
  const isDark = mode === 'dark';

  const background = pickString(colors.background, isDark ? '#0f0f0f' : '#f6f1e3');

  const foregroundCandidate = pickString(
    colors.text || colors.foreground || colors.headerText,
    isDark ? '#f5f5f5' : '#1a1814',
  );
  const foreground = resolveReadableTextColor(background, foregroundCandidate);
  // Keep the public shell on the same authored surface roles as the template
  // body. Generating these unconditionally made template-only branch pages
  // show one card/muted palette around a menu painted with another.
  const card = pickString(
    colors.surface || colors.panel || colors.card,
    mixCssColors(
      background,
      93,
      foreground,
      7,
      `color-mix(in srgb, ${background} 93%, ${foreground} 7%)`,
    ),
  );
  const muted = pickString(
    colors.secondary || colors.mutedSurface || colors.mutedBackground,
    mixCssColors(
      background,
      88,
      foreground,
      12,
      `color-mix(in srgb, ${background} 88%, ${foreground} 12%)`,
    ),
  );
  // Brand controls use the exact text role selected by the template. Adjust
  // only the fill shade if that pairing needs more contrast.
  const accent = colorReadableWithForeground(
    pickString(colors.accent || colors.price || colors.primary, isDark ? '#f5f5f5' : '#8a1f2d'),
    foreground,
  );
  const accentForeground = foreground;
  const primary = colorReadableWithForeground(
    pickString(colors.primary || colors.heading || colors.text, foreground),
    foreground,
  );
  const primaryForeground = foreground;
  const promoBg = pickString(colors.promoBg || colors.promotionBg, isDark ? 'rgba(255,255,255,0.08)' : '#fff0ea');
  const promoText = resolveReadableTextColor(
    promoBg,
    pickString(colors.promoText || colors.promotionText || colors.accent, accent),
  );
  const promoBorder = pickString(
    colors.promoBorder || colors.promotionBorder || colors.border || colors.divider,
    isDark ? 'rgba(245,245,245,0.16)' : 'rgba(26,24,20,0.12)',
  );
  const cardForeground = resolveReadableTextColor(
    card,
    pickString(colors.cardForeground || colors.surfaceForeground, foreground),
  );
  const mutedForeground = resolveReadableTextColor(
    muted,
    pickString(colors.mutedForeground || colors.mutedText || colors.muted, foreground),
  );
  const border = pickString(
    colors.divider || colors.panelBorder || colors.border,
    mixCssColors(
      foreground,
      14,
      background,
      86,
      `color-mix(in srgb, ${foreground} 14%, ${background} 86%)`,
    ),
  );
  const panelGlass = isDark
    ? mixCssColors(background, 84, 'black', 16, `color-mix(in srgb, ${background} 84%, black 16%)`)
    : mixCssColors(background, 86, 'white', 14, `color-mix(in srgb, ${background} 86%, white 14%)`);
  // Modal/sheet backdrop scrim, tinted with the template's own foreground color
  // instead of generic black -- keeps the same visual weight as the old bg-black/40
  // default while staying on-brand for both light and dark templates.
  const overlay = `color-mix(in srgb, ${foreground} 55%, transparent)`;

  return {
    background,
    foreground,
    muted,
    mutedForeground,
    card,
    cardForeground,
    border,
    accent,
    accentForeground,
    primary,
    primaryForeground,
    promoBg,
    promoText,
    promoBorder,
    panelGlass,
    panelShadow: isDark ? '0 18px 48px rgba(0,0,0,0.34)' : '0 18px 48px rgba(15,23,32,0.10)',
    overlay,
  };
}

/**
 * Maps a resolved surface palette onto the design-system `--color-*` tokens
 * used by the shared Input/Textarea/Select/Button controls. Portaled surfaces
 * (sheets, dialogs, modals) escape the page wrapper's CSS-variable cascade, so
 * callers re-apply these tokens on their own root to keep form controls themed.
 */
export function resolveColorVarsFromPalette(palette: TemplateSurfacePalette): CSSProperties {
  return {
    '--color-text-primary': palette.foreground,
    '--color-text-secondary': palette.mutedForeground,
    '--color-text-tertiary': `color-mix(in srgb, ${palette.foreground} 56%, ${palette.background} 44%)`,
    '--color-bg-base': palette.background,
    '--color-bg-surface': palette.card,
    '--color-bg-surface-hover': `color-mix(in srgb, ${palette.card} 84%, ${palette.accent} 16%)`,
    '--color-bg-sunken': palette.muted,
    '--color-bg-overlay': `color-mix(in srgb, ${palette.muted} 78%, transparent)`,
    '--color-border-default': palette.border,
    '--color-border-subtle': palette.border,
    '--color-border-strong': palette.border,
    '--color-accent': palette.accent,
    '--color-accent-text': palette.accentForeground,
    '--color-accent-hover': `color-mix(in srgb, ${palette.accent} 86%, ${palette.foreground} 14%)`,
    '--color-brand': palette.accent,
    '--color-brand-hover': `color-mix(in srgb, ${palette.accent} 86%, ${palette.foreground} 14%)`,
  } as CSSProperties;
}

export function resolveTemplateSurfaceStyle(
  template: Template | undefined | null,
  mode: TemplateThemeMode = 'light',
  palette: TemplateSurfacePalette = resolveTemplateSurfacePalette(template, mode),
): CSSProperties {
  const resolvedVariables = template ? resolveTemplateVariables(template, mode) : undefined;
  // normalizeFontFamily maps font names to their next/font CSS variables;
  // bare names never match the loaded families directly.
  const headingFont = normalizeFontFamily(String(
    (resolvedVariables as Record<string, any> | undefined)?.fonts?.heading ||
      (resolvedVariables as Record<string, any> | undefined)?.fonts?.display ||
      "'Playfair Display', serif",
  ));
  const bodyFont = normalizeFontFamily(String(
    (resolvedVariables as Record<string, any> | undefined)?.fonts?.body ||
      "'Inter', sans-serif",
  ));

  return {
    colorScheme: mode,
    fontFamily: bodyFont,
    '--background': toHslTriplet(palette.background),
    '--foreground': toHslTriplet(palette.foreground),
    '--card': toHslTriplet(palette.card),
    '--card-foreground': toHslTriplet(palette.cardForeground),
    '--popover': toHslTriplet(palette.card),
    '--popover-foreground': toHslTriplet(palette.cardForeground),
    '--primary': toHslTriplet(palette.primary),
    '--primary-foreground': toHslTriplet(palette.primaryForeground),
    '--secondary': toHslTriplet(palette.muted),
    '--secondary-foreground': toHslTriplet(palette.foreground),
    '--muted': toHslTriplet(palette.muted),
    '--muted-foreground': toHslTriplet(palette.mutedForeground),
    '--accent': toHslTriplet(palette.accent),
    '--accent-foreground': toHslTriplet(palette.accentForeground),
    '--border': toHslTriplet(palette.border),
    '--input-background': palette.card,
    '--mnrq-template-font-body': bodyFont,
    '--mnrq-template-font-heading': headingFont,
    '--mnrq-shell-bg': palette.background,
    '--mnrq-shell-fg': palette.foreground,
    '--mnrq-shell-muted': palette.muted,
    '--mnrq-shell-muted-fg': palette.mutedForeground,
    '--mnrq-shell-card': palette.card,
    '--mnrq-shell-card-fg': palette.cardForeground,
    '--mnrq-shell-border': palette.border,
    '--mnrq-shell-accent': palette.accent,
    '--mnrq-shell-accent-fg': palette.accentForeground,
    '--mnrq-shell-promo-bg': palette.promoBg,
    '--mnrq-shell-promo-text': palette.promoText,
    '--mnrq-shell-promo-border': palette.promoBorder,
    '--mnrq-shell-glass': palette.panelGlass,
    '--mnrq-shell-shadow': palette.panelShadow,
    '--mnrq-shell-overlay': palette.overlay,
    ...resolveColorVarsFromPalette(palette),
  } as CSSProperties;
}

function cloneCategoryWithoutPromotions(category: any) {
  return {
    ...category,
    promotion: undefined,
    items: Array.isArray(category?.items)
      ? category.items.map((item: any) => ({
          ...item,
          promotion: undefined,
        }))
      : [],
    featured_items: Array.isArray(category?.featured_items)
      ? category.featured_items.map((item: any) => ({
          ...item,
          promotion: undefined,
        }))
      : [],
  };
}

export function resolveGuestMenuRendererData(
  data: MenuData | undefined,
  options: {
    showPromotions: boolean;
    enableClaimAction: boolean;
  },
): MenuData | undefined {
  if (!data) return data;
  if (options.showPromotions) {
    return {
      ...data,
      actions: options.enableClaimAction ? data.actions : {},
    };
  }

  const categories = (data.menu?.categories || []).map(cloneCategoryWithoutPromotions);
  const items = (data.menu?.items || data.items || []).map((item: any) => ({
    ...item,
    promotion: undefined,
  }));
  const featuredItems = (data.menu?.featuredItems || data.featuredItems || []).map((item: any) => ({
    ...item,
    promotion: undefined,
  }));
  const collections = (data.menu?.collections || []).map((collection: any) => ({
    ...collection,
    promotions: [],
    categories: Array.isArray(collection?.categories)
      ? collection.categories.map(cloneCategoryWithoutPromotions)
      : [],
    items: Array.isArray(collection?.items)
      ? collection.items.map((item: any) => ({ ...item, promotion: undefined }))
      : [],
    featuredItems: Array.isArray(collection?.featuredItems)
      ? collection.featuredItems.map((item: any) => ({ ...item, promotion: undefined }))
      : [],
  }));

  return {
    ...data,
    menu: {
      ...data.menu,
      categories,
      items,
      featuredItems,
      collections,
      promotions: [],
    },
    items,
    featuredItems,
    promotions: [],
    actions: options.enableClaimAction ? data.actions : {},
  };
}
